import { useCartState } from "@app/_context";
import LoadingIconNew from "@components/Checkout/components/icons/LoadingIconNew";
import { Input, Loader } from "@components/ngg";
import type { ComponentProps } from "react";
import type { Dictionary } from "types";

import { TRANSITION_EASE_OUT } from "@/config/transitions";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@app/_icons/CheckIcon";
import { motion } from "framer-motion";
import useDiscount from "./useDiscount";

type Props = ComponentProps<"form"> & {
  dictionary: Dictionary;
};

export default function DiscountForm({
  dictionary,
  className,
  ...props
}: Props) {
  const {
    isDiscountCodeValid,
    isDiscountCodeApplicable,
    handleSubmit,
    inputRef,
    discountCode,
    handleChange,
  } = useDiscount();

  const { loading, error } = useCartState();

  return (
    <form
      onSubmit={(event) => handleSubmit(event)}
      className={cn(
        "mt-2 grid w-full grid-cols-[1fr_auto] grid-rows-[auto_auto] items-center gap-x-2",
        className,
      )}
      {...props}
    >
      <Input
        fullWidth
        classNames={{
          wrapper: "grid-rows-subgrid grid row-span-2 peer",
          input: "p-2 border-none placeholder:text-base",
          inputWrapper: "focus-within:border-black border flex items-center",
          hint: error.addPromotion ? "text-error" : "text-black",
        }}
        autoFocus
        ref={inputRef}
        type="text"
        required
        maxLength={15}
        name="discountCode"
        onChange={handleChange}
        placeholder={dictionary?.enterYourDiscountCode ?? "Ange rabattkod"}
        value={discountCode}
        readOnly={isDiscountCodeValid}
        hint={
          error.addPromotion
            ? dictionary?.invalidDiscountCode
            : !isDiscountCodeApplicable
              ? dictionary?.promotionNotApplied
              : undefined
        }
        endAdornment={isDiscountCodeValid && <CheckIcon className="mr-2" />}
      />

      <button
        disabled={!discountCode}
        type="submit"
        className="relative h-full min-w-24 bg-black px-4 text-center text-base tracking-wide text-white disabled:opacity-30"
      >
        {isDiscountCodeValid
          ? (dictionary.remove ?? "Remove")
          : (dictionary.apply ?? "Apply")}
        {loading.addPromotion && (
          <motion.div
            transition={TRANSITION_EASE_OUT}
            initial={{
              opacity: 0,
              backgroundColor: "rgba(0, 0, 0, 0)",
              backdropFilter: "blur(0px)",
            }}
            animate={{
              opacity: 1,
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              backdropFilter: "blur(2px)",
            }}
            exit={{
              opacity: 0,
              backgroundColor: "rgba(0, 0, 0, 0)",
              backdropFilter: "blur(0px)",
            }}
            layout
            className={cn(
              "pointer-events-none absolute inset-0 flex h-full w-full items-center justify-center",
            )}
          >
            <Loader
              LoaderComponent={<LoadingIconNew className="text-white/70" />}
            />
          </motion.div>
        )}
      </button>
    </form>
  );
}
